/**
 * The CASE 1 procedure, from NAVAIR 01-F14AAP-1 figure 8-3.
 *
 * Not a summary of it — the chart itself, item by item, in the order it puts
 * them. Where the trainer deviates it is noted in the step, because a
 * procedures trainer that quietly rearranges the procedure is worse than none.
 *
 * The chart's own layout, for reference:
 *
 *   inbound      wing sweep 68, 300-350 KIAS, 800 ft, HOOK DOWN and SAS ON
 *   the break    45-60 bank, speed brake extended, throttles idle,
 *                LEVEL turn at 800 ft (15-17 second interval)
 *   downwind     wing sweep 20, gear down below 280 KIAS
 *                slats/flaps extended below 225 KIAS
 *                DLC selected
 *                on-speed indexer (15 units), auto throttle engaged if desired
 *                landing checklist
 *                600 ft altitude
 *                cross check - gross weight, airspeed, angle of attack
 *   abeam        commence turn, 1 to 1.2 nmi
 *   the 90       indexer doughnut, 450 to 500 ft
 *   3/4 mile     intercept glide slope, 360 ft, on-speed
 *   groove       call: side number, Tomcat, ball/clara, state, type approach
 *
 * Three of these — DLC, the auto throttle and SAS — have no stock DCS keyboard
 * binding. They are bound here anyway and flagged `noDcsDefault`, because the
 * alternative is teaching a Tomcat approach with DLC left out of it.
 *
 * FIELDS
 *   leg       which leg of the pattern this belongs to
 *   key       bind id, or null for something you only have to notice
 *   satisfied predicate over the controls; how we know you did it
 *   hold      the pattern will not leave the leg until this is done
 *   optional  the chart says "if desired" — offered, never enforced
 *   cue       what to do, said the way an instructor would say it
 *   why       one line on why it matters
 */

/** @typedef {import('./PilotControls.js').PilotControls} Controls */

export const STEPS = [
  /* ---- inbound to the break ---------------------------------------- */
  {
    id: 'initial-look',
    leg: 'INITIAL',
    title: 'EYES ON THE BOAT',
    key: null,
    informational: true,
    cue: 'Eight hundred feet, 350 knots, up the starboard side. The wings are programming forward from 68 as you slow.',
    why: 'The break happens at the bow. Everything after it is timed off that point.',
    satisfied: () => true,
  },
  {
    id: 'initial-hook',
    leg: 'INITIAL',
    title: 'HOOK DOWN',
    key: 'hook',
    hold: true,
    cue: 'Hook down now, before the break — not on the downwind.',
    why: 'The chart puts it here, and there is a reason: the downwind is fifteen seconds with six other things in it. The hook is the one item you cannot recover from having forgotten.',
    satisfied: (c) => c.hookCmd > 0.5,
  },
  {
    id: 'initial-sas',
    leg: 'INITIAL',
    title: 'SAS ON',
    key: 'sas',
    hold: true,
    cue: 'Stability augmentation on. Pitch, roll and yaw.',
    why: 'The SAS dampers are what make the Tomcat steady enough to fly a ball. Without them the approach is a handful in exactly the regime you can least afford one.',
    satisfied: (c) => c.sasOn,
  },

  /* ---- the break ---------------------------------------------------- */
  {
    id: 'break-boards',
    leg: 'BREAK',
    title: 'SPEEDBRAKE OUT',
    key: 'brakeOut',
    hold: true,
    cue: 'Boards out as you roll into the break. Level turn, 45 to 60 degrees of bank.',
    why: '350 knots has to be approach speed by the abeam, and the break is nearly all the time you get.',
    satisfied: (c) => c.speedbrakeCmd > 0.5,
  },
  {
    id: 'break-idle',
    leg: 'BREAK',
    title: 'THROTTLES IDLE',
    key: 'throttleDown',
    keyHint: 'hold until the lever is at idle',
    hold: true,
    cue: 'Hold Page Down and bring the throttles to idle.',
    why: 'Boards alone will not do it. Idle and boards together is the whole deceleration.',
    satisfied: (c) => c.throttle < 0.12,
  },

  /* ---- downwind ----------------------------------------------------- */
  {
    id: 'dw-boards-in',
    leg: 'DOWNWIND',
    title: 'SPEEDBRAKE IN',
    key: 'brakeIn',
    hold: true,
    cue: 'Boards in, rolling out downwind at 600 feet.',
    why: 'Leave them out and you need far more power to hold on speed, which costs you the engine response you want at the ramp.',
    satisfied: (c) => c.speedbrakeCmd < 0.5,
  },
  {
    id: 'dw-sweep',
    leg: 'DOWNWIND',
    title: 'WING SWEEP — CHECK 20',
    key: null,
    informational: true,
    cue: 'Wings forward at 20 degrees. Nothing to press — the programmer does it from Mach.',
    why: 'It is on the chart as a check, not an action, and the day it matters is the day the programmer has failed and you are reaching for the emergency handle.',
    satisfied: () => true,
  },
  {
    id: 'dw-gear',
    leg: 'DOWNWIND',
    title: 'GEAR DOWN',
    key: 'gearDown',
    hold: true,
    cue: 'Below 280 knots — gear handle down.',
    why: 'Six seconds of travel, and nothing else on the checklist can be done until it is out.',
    satisfied: (c) => c.gearCmd > 0.5,
  },
  {
    id: 'dw-flaps',
    leg: 'DOWNWIND',
    title: 'SLATS AND FLAPS DOWN',
    key: 'flapsDown',
    hold: true,
    cue: 'Below 225 knots — slats and flaps extended.',
    why: 'Full flap is worth about 25 knots of approach speed. Without it there is no on-speed to fly.',
    satisfied: (c) => c.flapCmd > 0.5,
  },
  {
    id: 'dw-dlc',
    leg: 'DOWNWIND',
    title: 'DLC SELECTED',
    key: 'dlc',
    hold: true,
    cue: 'Direct lift control armed. The spoilers come alive on the thumbwheel.',
    why: 'DLC moves the aeroplane up and down without changing attitude or power. It is why a Tomcat flies the ball with a thumb rather than a throttle, and it is the single most F-14 thing on this list.',
    satisfied: (c) => c.dlcArmed,
  },
  {
    id: 'dw-power',
    leg: 'DOWNWIND',
    title: 'POWER FOR ON SPEED',
    key: 'throttleUp',
    keyHint: 'hold until the lever is around two thirds',
    hold: true,
    cue: 'Back up on the power — around 65 percent — and settle on the doughnut. Fifteen units.',
    why: 'Dirty and on speed you are on the back of the drag curve. Arrive at the abeam at idle and you will never catch it.',
    satisfied: (c) => c.throttle > 0.55,
  },
  {
    id: 'dw-apc',
    leg: 'DOWNWIND',
    title: 'AUTO THROTTLE — IF DESIRED',
    key: 'autoThrottle',
    optional: true,
    cue: 'Approach power compensator, if you want it. The chart says "if desired" and means it.',
    why: 'The APC holds angle of attack, not airspeed, which is the right thing to hold on an approach. Engaging it changes your ball call: the type of approach is part of the call.',
    satisfied: (c) => c.apcEngaged,
  },
  {
    id: 'dw-checklist',
    leg: 'DOWNWIND',
    title: 'LANDING CHECKLIST',
    key: null,
    informational: true,
    cue: 'Landing checklist complete. Gear, flaps, hook, DLC, harness, anti-skid.',
    why: 'The chart calls it out as one line because by this point everything on it should already be done. It is a confirmation, not a to-do list.',
    satisfied: () => true,
  },
  {
    id: 'dw-crosscheck',
    leg: 'DOWNWIND',
    title: 'CROSS CHECK',
    key: null,
    informational: true,
    cue: 'Gross weight, airspeed, angle of attack. Three numbers that have to agree with each other.',
    why: 'On speed is an ANGLE, and the airspeed that goes with it depends on what you weigh. If the three disagree, one of them is lying and you want to know which before the ball.',
    satisfied: () => true,
  },

  /* ---- the approach turn -------------------------------------------- */
  {
    id: 'turn-watch',
    leg: 'APPROACH_TURN',
    title: 'FLY THE TURN',
    key: null,
    informational: true,
    cue: 'Commence the turn at the abeam. The 90 is 450 to 500 feet on the doughnut; the glide slope comes to you at three quarters of a mile and 360 feet.',
    why: 'You are above the glideslope until then, which is why the ball is pegged high through the 90. Intercepting it is the manoeuvre — you do not sit on it from the abeam.',
    satisfied: () => true,
  },

  /* ---- the groove ---------------------------------------------------- */
  {
    id: 'ball-call',
    leg: 'GROOVE',
    title: 'CALL THE BALL',
    key: 'comms',
    hold: true,
    cue: 'Wings level in the groove — open the comms menu and call it.',
    /** Filled in at runtime: the type of approach depends on the auto throttle. */
    radio: (c) =>
      `Two-Zero-Four, Tomcat, ball, six-point-six, ${c?.apcEngaged ? 'auto' : 'manual'}.`,
    why: 'Side number, aircraft type, "ball", fuel state in thousands, type of approach. Say "clara" instead of "ball" if you cannot see it — the LSO needs to know that before anything else.',
    satisfied: (c) => c.ballCalled,
  },

  /* ---- touchdown ------------------------------------------------------ */
  {
    id: 'td-power',
    leg: 'TOUCHDOWN',
    title: 'FULL POWER',
    key: 'throttleUp',
    keyHint: 'hold to military as the wheels touch',
    hold: true,
    cue: 'Touchdown — throttles to military. Do not wait to see if you caught a wire.',
    why: 'You cannot tell a trap from a bolter until the aeroplane stops. Every landing is flown as a go-around that happens to be interrupted.',
    satisfied: (c) => c.throttle > 0.9,
  },

  /* ---- rollout --------------------------------------------------------- */
  {
    id: 'ro-idle',
    leg: 'ROLLOUT',
    title: 'THROTTLES IDLE',
    key: 'throttleDown',
    keyHint: 'hold until the lever is at idle',
    hold: true,
    cue: 'Stopped in the wires — throttles to idle.',
    why: 'You are attached to the ship by a cable with several thousand people around you.',
    satisfied: (c) => c.throttle < 0.12,
  },
  {
    id: 'ro-hook',
    leg: 'ROLLOUT',
    title: 'HOOK UP',
    key: 'hook',
    hold: true,
    cue: 'On the signal from the director — hook up.',
    why: 'The wire will not retract with the hook still in it, and the next aeroplane is about a minute behind you.',
    satisfied: (c) => c.hookCmd < 0.5,
  },
  {
    id: 'ro-flaps',
    leg: 'ROLLOUT',
    title: 'FLAPS UP',
    key: 'flapsUp',
    hold: true,
    cue: 'Flaps up, and you are clear to taxi out of the landing area.',
    why: 'Jet blast from the aeroplane behind you goes straight into a lowered flap.',
    satisfied: (c) => c.flapCmd < 0.5,
  },
]

export const STEPS_BY_LEG = STEPS.reduce((acc, s) => {
  ;(acc[s.leg] ??= []).push(s)
  return acc
}, {})

export const STEP_BY_ID = Object.fromEntries(STEPS.map((s) => [s.id, s]))

/** Steps that actually gate the pattern, i.e. the ones that can hold you up. */
export const HOLDING_STEPS = STEPS.filter((s) => s.hold)

/** The radio call for a step, which may depend on how the aeroplane is set up. */
export function radioFor(step, controls) {
  if (!step?.radio) return null
  return typeof step.radio === 'function' ? step.radio(controls) : step.radio
}
